import React, { useState, useRef, useEffect } from 'react';
import Button from '../../ui/Button';
import Spinner from '../../ui/Spinner';
import { filterDashboardDataByDate, getDateRangeSummary, getDateRange } from '../../../utils/qaqc/dateFilters';
import { generateQAQCReport } from '../../../utils/qaqc/pdfGenerator';

const QAQCReportsTab = ({ dashboardData, dashboardSummary, loadingStates }) => {
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [selectedSections, setSelectedSections] = useState({
    summaryStats: true,
    standardsDrift: true,
    standardsTrend: true,
    controlStandards: true,
    blanksAnalysis: true,
    duplicatesPrecision: true,
    failuresTable: true
  });
  const [selectedElements, setSelectedElements] = useState(['Au', 'Ag', 'Cu']);
  const [reportTitle, setReportTitle] = useState('QAQC Analysis Report');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState('');
  const [reportPreset, setReportPreset] = useState('full');
  
  const printContainerRef = useRef(null);

  // Calculate available date range from data
  const availableDateRange = React.useMemo(() => {
    const allData = [
      ...(dashboardData.controlStandards || []),
      ...(dashboardData.standardsDrift || []),
      ...(dashboardData.blanksDrift || []),
      ...(dashboardData.duplicatesDrift || [])
    ];
    return getDateRange(allData);
  }, [dashboardData]);

  // Check if any data is still loading
  const isLoading = Object.values(loadingStates || {}).some(state => state === true);

  // Apply preset configurations
  const applyPreset = (preset) => {
    setReportPreset(preset);
    switch (preset) {
      case 'full':
        setSelectedSections({
          summaryStats: true,
          standardsDrift: true,
          standardsTrend: true,
          controlStandards: true,
          blanksAnalysis: true,
          duplicatesPrecision: true,
          failuresTable: true
        });
        setSelectedElements(['Au', 'Ag', 'Cu']);
        break;
      case 'executive':
        setSelectedSections({
          summaryStats: true,
          standardsDrift: false,
          standardsTrend: false,
          controlStandards: false,
          blanksAnalysis: false,
          duplicatesPrecision: false,
          failuresTable: true
        });
        setSelectedElements(['Au', 'Ag', 'Cu']);
        break;
      case 'standards':
        setSelectedSections({
          summaryStats: true,
          standardsDrift: true,
          standardsTrend: true,
          controlStandards: true,
          blanksAnalysis: false,
          duplicatesPrecision: false,
          failuresTable: false
        });
        setSelectedElements(['Au', 'Ag', 'Cu']);
        break;
      case 'custom':
        // Keep current selections
        break;
      default:
        break;
    }
  };

  // Toggle section selection
  const toggleSection = (section) => {
    setSelectedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
    setReportPreset('custom');
  };

  // Toggle element selection
  const toggleElement = (element) => {
    setSelectedElements(prev => {
      if (prev.includes(element)) {
        return prev.filter(e => e !== element);
      } else {
        return [...prev, element];
      }
    });
    setReportPreset('custom');
  };

  // Handle report generation
  const handleGenerateReport = async () => {
    if (selectedElements.length === 0) {
      alert('Please select at least one element (Au, Ag, or Cu)');
      return;
    }

    const hasAnySection = Object.values(selectedSections).some(v => v === true);
    if (!hasAnySection) {
      alert('Please select at least one report section');
      return;
    }

    setIsGenerating(true);
    setGenerationProgress('Filtering data by date range...');

    try {
      // Filter data by date range
      const filteredData = filterDashboardDataByDate(
        dashboardData,
        dateRange.from,
        dateRange.to
      );

      const dateRangeSummary = getDateRangeSummary(dateRange.from, dateRange.to);

      // Generate PDF
      await generateQAQCReport({
        filteredData,
        selectedSections,
        selectedElements,
        reportTitle,
        dateRangeSummary,
        dashboardSummary,
        onProgress: setGenerationProgress,
        printContainerRef: printContainerRef.current
      });

      setGenerationProgress('Report generated successfully!');
      setTimeout(() => {
        setIsGenerating(false);
        setGenerationProgress('');
      }, 2000);
    } catch (error) {
      console.error('Error generating PDF report:', error);
      alert(`Failed to generate report: ${error.message}`);
      setIsGenerating(false);
      setGenerationProgress('');
    }
  };

  return (
    <div style={{ padding: '1rem' }}>
      <h2 style={{ marginBottom: '0.5rem' }}>PDF Report Generator</h2>
      <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '2rem' }}>
        Generate comprehensive QAQC analysis reports with customizable date ranges and sections.
      </p>

      {isLoading && (
        <div style={{ 
          padding: '1rem', 
          background: '#fef3c7', 
          border: '1px solid #fbbf24', 
          borderRadius: '6px',
          marginBottom: '1rem',
          color: '#92400e'
        }}>
          <strong>Note:</strong> Some data is still loading. The report will only include currently loaded data.
        </div>
      )}

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', 
        gap: '2rem',
        marginBottom: '2rem'
      }}>
        {/* Left Column - Report Configuration */}
        <div>
          {/* Report Title */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '0.875rem', 
              fontWeight: '600', 
              marginBottom: '0.5rem',
              color: '#f9fafb'
            }}>
              Report Title
            </label>
            <input
              type="text"
              value={reportTitle}
              onChange={(e) => setReportTitle(e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                background: '#374151',
                border: '1px solid #4b5563',
                borderRadius: '6px',
                color: '#f9fafb',
                fontSize: '0.875rem'
              }}
            />
          </div>

          {/* Date Range */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '0.875rem', 
              fontWeight: '600', 
              marginBottom: '0.5rem',
              color: '#f9fafb'
            }}>
              Date Range
            </label>
            {availableDateRange && (
              <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '0.5rem' }}>
                Available data: {availableDateRange.minDate} to {availableDateRange.maxDate}
              </p>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              <div>
                <label style={{ fontSize: '0.75rem', color: '#9ca3af', display: 'block', marginBottom: '0.25rem' }}>
                  From
                </label>
                <input
                  type="date"
                  value={dateRange.from}
                  onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                  min={availableDateRange?.minDate}
                  max={availableDateRange?.maxDate}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    background: '#374151',
                    border: '1px solid #4b5563',
                    borderRadius: '6px',
                    color: '#f9fafb',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
              <div>
                <label style={{ fontSize: '0.75rem', color: '#9ca3af', display: 'block', marginBottom: '0.25rem' }}>
                  To
                </label>
                <input
                  type="date"
                  value={dateRange.to}
                  onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                  min={availableDateRange?.minDate || dateRange.from}
                  max={availableDateRange?.maxDate}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    background: '#374151',
                    border: '1px solid #4b5563',
                    borderRadius: '6px',
                    color: '#f9fafb',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
            </div>
            {!dateRange.from && !dateRange.to && (
              <p style={{ fontSize: '0.75rem', color: '#06b6d4', marginTop: '0.5rem' }}>
                Leave empty to include all data
              </p>
            )}
          </div>

          {/* Report Presets */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '0.875rem', 
              fontWeight: '600', 
              marginBottom: '0.5rem',
              color: '#f9fafb'
            }}>
              Report Preset
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {[
                { id: 'full', label: 'Full Report' },
                { id: 'executive', label: 'Executive Summary' },
                { id: 'standards', label: 'Standards Only' },
                { id: 'custom', label: 'Custom' }
              ].map(preset => (
                <button
                  key={preset.id}
                  onClick={() => applyPreset(preset.id)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    background: reportPreset === preset.id ? '#06b6d4' : '#374151',
                    color: reportPreset === preset.id ? '#0c4a6e' : '#f9fafb',
                    border: '1px solid',
                    borderColor: reportPreset === preset.id ? '#06b6d4' : '#4b5563',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: reportPreset === preset.id ? '600' : '400',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          {/* Element Selection */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '0.875rem', 
              fontWeight: '600', 
              marginBottom: '0.5rem',
              color: '#f9fafb'
            }}>
              Elements to Include
            </label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {['Au', 'Ag', 'Cu'].map(element => (
                <label key={element} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedElements.includes(element)}
                    onChange={() => toggleElement(element)}
                    style={{ cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '0.875rem', color: '#f9fafb' }}>{element}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column - Section Selection */}
        <div>
          <label style={{ 
            display: 'block', 
            fontSize: '0.875rem', 
            fontWeight: '600', 
            marginBottom: '0.5rem',
            color: '#f9fafb'
          }}>
            Report Sections
          </label>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '0.75rem',
            background: '#1f2937',
            padding: '1rem',
            borderRadius: '6px',
            border: '1px solid #374151'
          }}>
            {[
              { id: 'summaryStats', label: 'Summary Statistics', description: 'Key metrics and pass rates' },
              { id: 'standardsDrift', label: 'Standards Drift Analysis', description: 'EWMA and drift detection per element' },
              { id: 'standardsTrend', label: 'Standards Long-Term Trend', description: 'Monthly bias trends' },
              { id: 'controlStandards', label: 'Control Standards (Z-Scores)', description: 'Individual CRM performance' },
              { id: 'blanksAnalysis', label: 'Blanks Contamination Analysis', description: 'Blank samples per element' },
              { id: 'duplicatesPrecision', label: 'Duplicates Precision Analysis', description: 'RPD analysis per element' },
              { id: 'failuresTable', label: 'Failures Summary Table', description: 'Unresolved QC failures' }
            ].map(section => (
              <label 
                key={section.id} 
                style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '0.75rem', 
                  cursor: 'pointer',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  background: selectedSections[section.id] ? 'rgba(6, 182, 212, 0.1)' : 'transparent',
                  border: '1px solid',
                  borderColor: selectedSections[section.id] ? '#06b6d4' : 'transparent'
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedSections[section.id]}
                  onChange={() => toggleSection(section.id)}
                  style={{ cursor: 'pointer', marginTop: '0.25rem' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.875rem', color: '#f9fafb', fontWeight: '500' }}>
                    {section.label}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.125rem' }}>
                    {section.description}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Generation Button and Progress */}
      <div style={{ 
        borderTop: '1px solid #374151', 
        paddingTop: '1.5rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <Button
          onClick={handleGenerateReport}
          disabled={isGenerating || selectedElements.length === 0}
          variant="primary"
          style={{
            padding: '0.75rem 2rem',
            fontSize: '1rem',
            fontWeight: '600',
            alignSelf: 'flex-start'
          }}
        >
          {isGenerating ? 'Generating Report...' : 'Generate PDF Report'}
        </Button>

        {isGenerating && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            padding: '1rem',
            background: '#1f2937',
            borderRadius: '6px',
            border: '1px solid #374151'
          }}>
            <Spinner />
            <div>
              <div style={{ fontSize: '0.875rem', color: '#f9fafb', fontWeight: '500' }}>
                Generating PDF Report
              </div>
              <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                {generationProgress}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Hidden render container for chart capture */}
      <div 
        ref={printContainerRef} 
        style={{ 
          position: 'absolute', 
          left: '-9999px', 
          top: 0,
          width: '800px',
          background: 'white'
        }}
      />
    </div>
  );
};

export default QAQCReportsTab;
